// DocumentFrame.tsx — a real KB document, rendered ON the board.
//
// WHY (Erez, 2026-07-25): a sidebar knowledge app "puts things back in the
// drawer". He wants the document itself in front of him on the canvas — zoom
// in, scroll it, read it — not a folder tree he has to open. So a document is
// a FIXED-HEIGHT framed panel that scrolls its own content, and several sit
// side by side inside ONE SectionBox.
//
// The content is a page_content row (the same BlockRenderer recipe the
// /kb-doc/* route renders), fetched by slug. That means the document is DATA:
// an agent edits it with update_kb_page and the board reflects it with no
// deploy. Shared component — every board imports it, nobody re-implements it.
//
// THE TWO REACT-FLOW TRAPS this file exists to get right:
//   1. React Flow sets `pointer-events: none` on non-draggable nodes, which
//      silently kills scrolling inside them → we force `pointerEvents: auto`.
//   2. A wheel event inside a node pans/zooms the CANVAS instead of scrolling
//      the panel → React Flow honours the `nowheel` class to opt out, and
//      `nodrag nopan` stops drag-to-pan starting on the text.
// Both are why this is a component and not a div someone re-copies per board.
//
// Deliberately NOT an iframe of /kb-doc/*, and it does not touch the shared
// Layout — that exact move hung the whole dashboard in July 2026.

import { useEffect, useState } from "react";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";
import { useApi } from "../../../lib/api";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Hebrew / Arabic and neighbouring RTL ranges.
//
// NOTE this is deliberately NOT the KB route's "one RTL character → RTL page"
// test. That works for a wholly-Hebrew page, but these are English documents
// that quote a few Hebrew terms (פלקון נדל״ן, הובלה), and a single match flipped
// the entire document right-to-left — punctuation landing at the start of every
// line (caught on the Falcon board, 2026-07-25). So: measure the PROPORTION of
// RTL letters and only flip when the document is actually mostly RTL.
const RTL_CHARS = /[֐-޿ࠀ-ࣿﭐ-﷿ﹰ-﻿]/g;
const LATIN_CHARS = /[A-Za-z]/g;

function detectDir(blocks: Block[]): "ltr" | "rtl" {
  const text = JSON.stringify(blocks);
  const rtl = (text.match(RTL_CHARS) ?? []).length;
  if (rtl === 0) return "ltr";
  const latin = (text.match(LATIN_CHARS) ?? []).length;
  // JSON keys and block type names are Latin, so a genuinely Hebrew document
  // still clears this comfortably while a few quoted terms do not.
  return rtl / (rtl + latin) > 0.2 ? "rtl" : "ltr";
}

export function DocumentFrame({
  slug,
  label,
  width = 860,
  height = 1180,
}: {
  /** page_content slug, e.g. "kb-doc/consultancy/falcon/engagement" */
  slug: string;
  /** Small caption above the sheet. Falls back to the page's nav_label. */
  label?: string;
  width?: number;
  /** Fixed frame height — the document scrolls inside it. */
  height?: number;
}) {
  const api = useApi();
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [navLabel, setNavLabel] = useState<string | null>(null);
  const [dir, setDir] = useState<"ltr" | "rtl">("ltr");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBlocks(null);
    setError(null);
    (async () => {
      try {
        const res = await api(`/api/kb/${slug}`);
        if (!res.ok) {
          if (!cancelled) setError(`${res.status} — could not load ${slug}`);
          return;
        }
        const body = (await res.json()) as {
          content?: { blocks?: Block[]; dir?: "ltr" | "rtl" };
          nav_label?: string | null;
        };
        if (cancelled) return;
        if (Array.isArray(body.content?.blocks)) {
          const b = body.content!.blocks!;
          setBlocks(b);
          setNavLabel(body.nav_label ?? null);
          setDir(body.content?.dir ?? detectDir(b));
        } else {
          setError(`no blocks at ${slug}`);
        }
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // `api` is NOT a dependency on purpose: useApi() returns a fresh closure on
    // every render, so including it re-runs this effect forever and the frame
    // never leaves the loading state. Same reason KbBlueprintPage keys on slug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const caption = label ?? navLabel ?? slug.split("/").pop() ?? "Document";

  return (
    <div style={{ width, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 10 }}>
      {/* caption strip — tells you which document you're looking at without
          opening anything, and carries the deep link for when you want it */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingInline: 4 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8C948E" }}>
          {caption}
        </div>
        <a
          href={`/${slug}`}
          className="nodrag nopan"
          style={{ pointerEvents: "auto", fontFamily: MONO, fontSize: 11, color: "#8C948E", textDecoration: "none", whiteSpace: "nowrap" }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          open ↗
        </a>
      </div>

      {/* the sheet — a page, scrolling inside a fixed height */}
      <div
        // nowheel: scroll the sheet, don't zoom the canvas.
        // nodrag/nopan: don't start a canvas drag on the text.
        className="nowheel nodrag nopan"
        // Double-click anywhere on the sheet frames THIS document (BoardCanvas).
        // Tradeoff accepted: that overrides double-click-to-select-a-word inside
        // the page. Reading the document at size is what this is for.
        data-zoom
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          pointerEvents: "auto",
          width,
          height,
          overflowY: "auto",
          overflowX: "hidden",
          background: "#FFFFFF",
          border: "1px solid #E3DED4",
          borderRadius: 18,
          boxShadow: "0 10px 30px rgba(27,33,30,0.05)",
          // room to breathe like a real page, not a card
          padding: "44px 52px 56px",
          boxSizing: "border-box",
        }}
      >
        {error && (
          <div style={{ fontFamily: MONO, fontSize: 13, color: "#C0492F" }}>{error}</div>
        )}
        {!error && !blocks && (
          <div style={{ fontFamily: MONO, fontSize: 13, color: "#8C948E" }}>loading…</div>
        )}
        {blocks && <BlockRenderer config={architectureConfig} blocks={blocks} dir={dir} />}
      </div>
    </div>
  );
}
